import { defaultScoutcardRequest, scoutcardRequestSchema, ScoutcardRequestType } from '@/api/scoutcard'
import { openScoutcardRequestsQueryOptions, useCreateScoutcardRequestMutation } from '@/api/scoutcardOptions'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import CreateDetailAccordion, { useCreateDetail } from '@/components/detail/CreateDetailRecord'
import LoadProgress from '@/components/LoadProgress'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import ScoutIdAutocompleteField from '@/components/ScoutIdAutocompleteField'
import { yupResolver } from '@hookform/resolvers/yup'
import { Box, Grid } from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { t } from 'i18next'
import { enqueueSnackbar } from 'notistack'

export const Route = createFileRoute('/_auth/scoutcardRequests')({
    component: ScoutcardRequestsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function ScoutcardRequestsComponent() {

    const scoutcardRequestQuery = useSuspenseQuery(
        openScoutcardRequestsQueryOptions(),
    )
    const scoutcardRequests = scoutcardRequestQuery.data

    const onSuccess = () => {
        enqueueSnackbar(t('message.scoutcard.request.success'), { variant: 'success' });
    }
    const onError = () => {
        enqueueSnackbar(t('message.scoutcard.request.error'), { variant: 'error' });
    }
    const createScoutcardRequestMutation = useCreateScoutcardRequestMutation(onSuccess, onError)

    const columns: GridColDef[] = []

    columns.push(
        {
            field: 'member.scoutId',
            headerName: 'Scout-ID',
            valueGetter: (_params, row) => row.member?.scoutId,
            minWidth: 150,
        },
        {
            field: 'member.firstname',
            headerName: 'Vorname',
            valueGetter: (_params, row) => row.member?.firstname,
            minWidth: 100,
        },
        {
            field: 'member.lastname',
            headerName: 'Nachname',
            valueGetter: (_params, row) => row.member?.lastname,
            minWidth: 100,
        },
    )

    const onSubmit = (data: ScoutcardRequestType) => {
        createScoutcardRequestMutation.mutate({ uuid: '', member: data.member } as ScoutcardRequestType);
    }

    const createDetail = useCreateDetail<ScoutcardRequestType>({
        onSubmit,
        resolver: yupResolver<ScoutcardRequestType, any, any>(scoutcardRequestSchema),
        defaultValues: {
            ...defaultScoutcardRequest,
        },
        title: t('action.new', { entity: t('entity.scoutcardRequest') }),
    })
    const createable = true

    if (
        scoutcardRequestQuery.isLoading ||
        scoutcardRequestQuery.isFetching ||
        !scoutcardRequests
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            {createable && (
                <CreateDetailAccordion defaultExpanded={true} {...createDetail}>
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 12,
                                md: 6,
                                lg: 4
                            }}>
                            <ScoutIdAutocompleteField
                                name="member"
                                defaultValue={defaultScoutcardRequest.member.uuid}
                                control={createDetail.form.control}
                                errors={createDetail.form.formState.errors}
                            ></ScoutIdAutocompleteField>
                        </Grid>
                    </Grid>
                </CreateDetailAccordion>
            )
            }
            <DataGridPremium
                columns={columns}
                rows={scoutcardRequests}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                slots={{
                    toolbar: (props) => {
                        return <OverviewGridToolbar {...props} scope="scoutcardRequests"  >
                        </OverviewGridToolbar>
                    }
                }}
                slotProps={{
                    toolbar: {
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    }
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableColumnReorder
                disableRowGrouping
                disableRowSelectionOnClick
                disablePivoting
                showToolbar />
        </Box >
    );
}